/**
 * Spatial voice chat, and the muttering of everyone who has not got a
 * microphone.
 *
 * This is the seam the network layer talks to. It owns:
 *
 *  - one `PeerAudio` per player on your floor, which places whatever they are
 *    making in the world (`voicepeer.ts`),
 *  - a WebRTC mesh, one connection per peer close enough to matter
 *    (`voicelink.ts`),
 *  - the microphone and the once-ever question about it (`voicemic.ts`),
 *  - and the synthesised person each peer is when nothing is arriving from them
 *    (`mutter.ts`).
 *
 * It is entirely optional. Nothing here runs until `attach` is called with a
 * live socket, and every path through it — no WebRTC, no microphone, a refused
 * permission, a server that never comes up — ends in the game sounding exactly
 * as it did before multiplayer existed. That is not a nicety: the site must
 * never break because a Worker is down.
 *
 * The frame update is driven by the audio engine rather than by `game.ts`,
 * because it needs the listener to have been placed already and the audio graph
 * is where that happens.
 */

import type { GameContext } from '../core/types';
import type { PeerId } from '../net/protocol';
import { VOICE_FAR, VOICE_NEAR } from '../net/protocol';
import type { AudioGraph } from './graph';
import { PeerAudio } from './voicepeer';
import { isPolite, VoiceLink, webrtcSupported } from './voicelink';
import { isTyping, micSupported, Microphone } from './voicemic';

/**
 * One peer, as much of `PeerState` as the voice layer needs. Structural on
 * purpose: the network client can hand its `peers` array straight in.
 */
export interface VoicePeer {
  id: PeerId;
  x: number;
  z: number;
}

/**
 * The little the voice layer needs from the socket. Signalling blobs are opaque
 * to everyone in between, so this is the whole contract.
 */
export interface VoiceTransport {
  /**
   * Our own id, or null before `welcome`. Read on every use rather than
   * captured, so this can be a getter straight onto the network client and
   * `attach` need not wait for the handshake — no connection is opened until
   * there is an id to be polite about.
   */
  readonly self: PeerId | null;
  /** Relay `data` to one peer as a `signal` message. */
  send(to: PeerId, data: unknown): void;
  /** Subscribe to inbound `signal` messages. Returns an unsubscribe. */
  onSignal(fn: (from: PeerId, data: unknown) => void): () => void;
}

/** What the network layer and the game may do with the voice chat. */
export interface VoiceChat {
  /** Start. Safe to call as soon as the network client exists. */
  attach(transport: VoiceTransport): void;
  /** Stop, and let go of the microphone. Safe to call when not attached. */
  detach(): void;
  /** The current floor's peers. Call whenever a `peers` message lands. */
  setPeers(peers: readonly VoicePeer[]): void;
  /** True while a microphone is open and unmuted. */
  readonly transmitting: boolean;
  /** Toggle the transmit mute. Bound to V; here so a menu can do it too. */
  toggleMute(): void;
}

/** Metres past `VOICE_FAR` a connection is kept alive before being dropped. */
const LINK_SLACK = 10;

/** Seconds a peer must stay out of range before its connection is closed. */
const LINK_DROP_DELAY = 5;

/** Seconds without a `setPeers` call before the world is assumed empty. */
const PEERS_STALE = 3;

/** Seconds with nobody in voice range before the microphone is released. */
const MIC_IDLE = 45;

interface LinkEntry {
  link: VoiceLink;
  /** Seconds the peer has been out of range. Reset whenever they are near. */
  away: number;
}

export class VoiceChatSystem implements VoiceChat {
  private graph: AudioGraph | null = null;
  private transport: VoiceTransport | null = null;
  private offSignal: (() => void) | null = null;

  /** Where each peer is, as last reported. The authority for who exists. */
  private readonly wanted = new Map<PeerId, VoicePeer>();
  private readonly peers = new Map<PeerId, PeerAudio>();
  private readonly links = new Map<PeerId, LinkEntry>();
  /**
   * Streams that arrived before the peer did. A connection can come up from an
   * inbound offer a tick or two before the `peers` message that names its
   * sender, and dropping the stream in that window would cost the whole
   * conversation.
   */
  private readonly earlyStreams = new Map<PeerId, MediaStream>();

  private readonly mic: Microphone;
  private bus: GameContext['events'] | null = null;
  private peersAt = 0;
  private idleFor = 0;
  private disposed = false;

  constructor() {
    this.mic = new Microphone({
      onOpened: () => this.publishMic(),
      bus: () => this.bus,
    });
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.onKey, true);
  }

  get transmitting(): boolean {
    return this.mic.available && !this.mic.isMuted;
  }

  /* ---------------------------------------------------------------- *
   * The network seam
   * ---------------------------------------------------------------- */

  attach(transport: VoiceTransport): void {
    if (this.disposed) return;
    this.detach();
    this.transport = transport;
    this.offSignal = transport.onSignal((from, data) => this.onSignal(from, data));
  }

  detach(): void {
    this.offSignal?.();
    this.offSignal = null;
    this.transport = null;
    for (const e of this.links.values()) e.link.close();
    this.links.clear();
    for (const p of this.peers.values()) p.dispose();
    this.peers.clear();
    this.wanted.clear();
    this.earlyStreams.clear();
    this.idleFor = 0;
    // Holding the microphone open with nobody to hear it leaves the browser's
    // recording indicator on for no reason.
    this.mic.release();
  }

  setPeers(peers: readonly VoicePeer[]): void {
    if (this.disposed) return;
    this.peersAt = now();
    this.wanted.clear();
    for (const p of peers) {
      if (this.transport && p.id === this.transport.self) continue;
      this.wanted.set(p.id, { id: p.id, x: p.x, z: p.z });
    }
  }

  toggleMute(): void {
    this.mic.toggleMute();
  }

  /* ---------------------------------------------------------------- *
   * Engine-facing
   * ---------------------------------------------------------------- */

  /** The audio graph appeared (or went away with the context). */
  setGraph(g: AudioGraph | null): void {
    if (g === this.graph) return;
    for (const p of this.peers.values()) p.dispose();
    this.peers.clear();
    this.graph = g;
  }

  /**
   * Per frame, from `Engine.update`, after the listener has been placed.
   *
   * `dt` is wall-clock rather than simulation time, so the muttering keeps its
   * pacing across a frame the game chose not to simulate.
   */
  update(dt: number, ctx: GameContext): void {
    if (this.disposed) return;
    this.bus = ctx.events;
    this.mic.update(dt);

    const g = this.graph;
    if (!g) return;

    // A socket that stopped talking is not a floor that emptied, but it is
    // indistinguishable from here, and a peer frozen mid-corridor muttering
    // forever is much worse than a moment of silence.
    const fresh = now() - this.peersAt < PEERS_STALE;
    const active = fresh && !ctx.paused && !ctx.dead;

    this.reconcile(g);

    let nearest = Infinity;
    for (const p of this.peers.values()) {
      p.update(dt, active);
      if (p.distance < nearest) nearest = p.distance;
    }

    this.updateLinks(dt, active);

    // The question, asked exactly where the design says to ask it: the first
    // time somebody is close enough to talk to, and never on load.
    if (active && nearest <= VOICE_NEAR) this.mic.askIfNeeded();

    // Nobody in earshot for a long time: give the microphone back. Re-opening
    // it later is silent, because the permission has already been granted.
    if (nearest > VOICE_FAR + LINK_SLACK) {
      this.idleFor += dt;
      if (this.idleFor > MIC_IDLE && this.mic.available) this.mic.release();
    } else {
      this.idleFor = 0;
    }
  }

  dispose(): void {
    this.disposed = true;
    if (typeof window !== 'undefined') window.removeEventListener('keydown', this.onKey, true);
    this.detach();
    this.mic.dispose();
    this.graph = null;
    this.bus = null;
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /** Bring the set of live audio chains in line with the set of live peers. */
  private reconcile(g: AudioGraph): void {
    for (const [id, w] of this.wanted) {
      let p = this.peers.get(id);
      if (!p) {
        p = new PeerAudio(id, g);
        this.peers.set(id, p);
        const early = this.earlyStreams.get(id);
        if (early) {
          p.setStream(early);
          this.earlyStreams.delete(id);
        }
      }
      p.setPosition(w.x, w.z);
    }
    for (const [id, p] of this.peers) {
      if (this.wanted.has(id)) continue;
      p.dispose();
      this.peers.delete(id);
      this.dropLink(id);
    }
  }

  /**
   * Open a connection to anyone we could be heard by, and close the ones to
   * people who have walked away.
   *
   * We only ever *initiate* when we have something to send. Two players who
   * both declined the microphone never establish a connection at all, which is
   * the common case and costs nothing — they still hear each other, because
   * `mutter.ts` does not need a connection to know somebody is there.
   */
  private updateLinks(dt: number, active: boolean): void {
    const mine = this.mic.available ? this.mic.stream : null;

    for (const [id, p] of this.peers) {
      const inRange = active && p.distance <= VOICE_FAR;
      const entry = this.links.get(id);
      if (entry) {
        entry.away = inRange || p.distance <= VOICE_FAR + LINK_SLACK ? 0 : entry.away + dt;
        if (entry.away > LINK_DROP_DELAY) this.dropLink(id);
        continue;
      }
      if (mine && inRange) this.openLink(id)?.setLocalStream(mine);
    }
  }

  /** Someone signalled us. Build the link if we do not already have one. */
  private onSignal(from: PeerId, data: unknown): void {
    if (this.disposed || !this.transport) return;
    const link = this.openLink(from);
    if (!link) return;
    // If we have a microphone, answer with it: the other end started this
    // because they want to talk, and there is no reason to be one-way.
    if (this.mic.available) link.setLocalStream(this.mic.stream);
    link.accept(data);
  }

  /** Idempotent: returns the existing connection when there already is one. */
  private openLink(id: PeerId): VoiceLink | null {
    const existing = this.links.get(id);
    if (existing) return existing.link;
    const t = this.transport;
    // No id yet means no way to decide which end is the polite one, and glare
    // without that is two connections that both give up.
    if (!t || !t.self || !webrtcSupported()) return null;
    const self = t.self;

    const link = new VoiceLink(isPolite(self, id), {
      send: (data) => this.transport?.send(id, data),
      onStream: (stream) => this.receive(id, stream),
      onFailed: () => this.dropLink(id),
    });
    if (!link.ok) {
      link.close();
      return null;
    }
    this.links.set(id, { link, away: 0 });
    return link;
  }

  private receive(id: PeerId, stream: MediaStream | null): void {
    const p = this.peers.get(id);
    if (p) p.setStream(stream);
    else if (stream) this.earlyStreams.set(id, stream);
  }

  private dropLink(id: PeerId): void {
    const entry = this.links.get(id);
    this.earlyStreams.delete(id);
    if (!entry) return;
    entry.link.close();
    this.links.delete(id);
    this.peers.get(id)?.setStream(null);
  }

  /** The player just granted the microphone; offer it to everyone in range. */
  private publishMic(): void {
    const mine = this.mic.stream;
    if (!mine) return;
    for (const [id, p] of this.peers) {
      if (p.distance > VOICE_FAR) continue;
      this.openLink(id)?.setLocalStream(mine);
    }
  }

  /**
   * B mutes and unmutes. Not routed through `core/input.ts` because it is not a
   * game action — it has to work with the pause menu up, and it must never be
   * remappable onto something the player needs mid-chase. Capture phase and an
   * `isTyping` guard so it cannot fire into a chat box.
   *
   * It was V, which is now `attack` in `core/input.ts`: holding a key to swing
   * at somebody and tapping the same key to stop talking to them is one key
   * doing two jobs badly. B is next to it and bound to nothing.
   */
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'KeyB' || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
    if (!this.mic.available || isTyping()) return;
    e.preventDefault();
    this.mic.toggleMute();
  };
}

function now(): number {
  return performance.now() / 1000;
}

/**
 * Build the voice chat. Cheap and side-effect free: it opens no sockets, takes
 * no permissions and touches no audio until it is attached and somebody walks
 * up to you.
 */
export function makeVoiceChat(): VoiceChatSystem {
  return new VoiceChatSystem();
}

/** True when this browser could support voice at all. Neither is required. */
export function voiceSupported(): boolean {
  return webrtcSupported() && micSupported();
}
